/* eslint-disable camelcase */
import { useMutation } from '@tanstack/react-query'
import { AxiosError } from 'axios'
import { aquaApi } from '../../../../api/aquaApi'
import { paths } from '../../../../api/pathsApi'
import { handleAxiosCodeError, handleAxiosError } from '../../../../helpers/handleAxiosError'
import { ImagesBody } from '../pages/Summary/types'
import { useContext } from 'react'
import { FuelContext } from '../../../../context/Logistics/fuel/FuelProvider'
import { AuthContext } from '../../../../context/Auth/AuthContext'
import { useBlobAzure } from '../../../../hooks/useAzureSaveImage'

const saveData = async (values: { body: ImagesBody; orderId: number }) => {
  const { data } = await aquaApi.post<unknown>(
    paths().LOGISTIC.CREATE_RECORD(values.orderId),
    values.body,
  )
  return data
}

interface Args {
  onSuccess?: (data: unknown, body: ImagesBody) => void
  onError?: (values: { msg: string; code: number | null }) => void
}

export const useSendRecord = ({ onSuccess, onError }: Args) => {
  const {
    user: { userId },
  } = useContext(AuthContext)
  const { params, form } = useContext(FuelContext)
  const { isUploading, uploadFiles } = useBlobAzure({ containerName: 'logistic' })
  const { data, error, isPending, mutate, isSuccess } = useMutation({
    mutationFn: (values: { body: ImagesBody; orderId: number }) => saveData(values),
    onSuccess(data, body) {
      if (onSuccess) {
        onSuccess(data, body.body)
      }
    },
    onError(error: AxiosError) {
      console.log(error.response?.data)
      if (onError) {
        onError({
          msg: handleAxiosError(error),
          code: handleAxiosCodeError(error),
        })
      }
    },
  })

  const handleSendRecord = async (orderId: number) => {
    const files = Object.values(form.photos!).filter((photo) => photo.uri !== '')
    const azureResponse = await uploadFiles(
      files.map((photo, index) => ({
        imageName: form.orderNumber + '-' + (index + 1),
        imageUri: photo.uri,
      })),
    )
    if (azureResponse.length === 0) {
      if (onError) {
        onError({
          msg: 'Error al subir imagen al storage',
          code: 500,
        })
      }
      return
    }
    mutate({
      orderId: orderId,
      body: {
        id_user: userId!,
        files: azureResponse.map((url, index) => {
          const date = new Date(+files[index]!.dateTime)
          const utcDate = date.toISOString()
          return {
            id_resource_type:
              params.resourseTypes.find((resource) =>
                resource.name.includes(files[index]!.resourceType),
              )?.id || 0,
            url,
            timestamp: utcDate,
          }
        }),
      },
    })
  }

  return {
    handleSendRecord,
    data,
    isLoading: isPending || isUploading,
    isSuccess,
    msgError: handleAxiosError(error as AxiosError),
    codeError: handleAxiosCodeError(error as AxiosError),
  }
}
